import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { OwnedBanner } from '@/components/OwnedBanner'
import { EventAttendees } from '@/components/events/event-attendees'
import { EventDetailActions } from '@/components/events/event-cancellation'
import { eventApiMessage } from '@/components/events/event-errors'
import { EventOrders } from '@/components/events/event-orders'
import { EventReportActions } from '@/components/events/event-report-actions'
import { EventSettings, isOwnedEvent } from '@/components/events/event-settings'
import type { TaxRateSummary } from '@/components/events/event-ticket-types'
import { EventTurnout } from '@/components/events/event-turnout'
import { EventWaitlist } from '@/components/events/event-waitlist'
import { DetailPage } from '@/components/templates/detail-page'
import { useViewOnly } from '@/components/templates/app-shell'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getJSON, patchJSON } from '@/lib/api'
import type { EventRecord } from '@/pages/event-types'
import { EVENT_TABS, useEventTab, type EventTab } from '@/pages/useEventTab'

export function useEvent(eventId: number) {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: () => getJSON<{ event: EventRecord }>(`/api/v1/events/${eventId}`),
    retry: false,
  })
}

export type EventDetailNavigation = {
  onRegistrationsReport: () => void
  onSegment?: () => void
  onBack: () => void
  onNewOrder: () => void
  onCheckIn: () => void
  onDeleted: () => void
}

/** One Event, opened on its Orders: Orders, Attendees, Waitlist and Settings tabs. */
export function EventDetail({
  eventId,
  ...navigation
}: { eventId: number } & EventDetailNavigation) {
  const queryClient = useQueryClient()
  const query = useEvent(eventId)
  const taxRatesQuery = useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => getJSON<{ tax_rates: TaxRateSummary[] }>('/api/v1/tax_rates'),
    retry: false,
  })
  const transition = (action: 'publish' | 'unpublish' | 'cancel') => ({
    mutationFn: () => patchJSON<{ event: EventRecord }>(`/api/v1/events/${eventId}/${action}`, {}),
    onSuccess: ({ event }: { event: EventRecord }) => {
      queryClient.setQueryData(['event', eventId], { event })
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
  const publish = useMutation(transition('publish'))
  const unpublish = useMutation(transition('unpublish'))
  const cancel = useMutation(transition('cancel'))
  const event = query.data?.event
  if (!event)
    return (
      <DetailPage title="Event" subtitle={query.isLoading ? 'Loading…' : 'Event unavailable'}>
        <div />
      </DetailPage>
    )
  return (
    <EventWorkspace
      event={event}
      navigation={navigation}
      taxRates={taxRatesQuery.data?.tax_rates ?? []}
      taxRatesState={taxRateQueryState(taxRatesQuery.isLoading, taxRatesQuery.isError)}
      mutationError={publish.error ?? unpublish.error ?? cancel.error}
      header={
        <EventDetailActions
          event={event}
          publishing={publish.isPending}
          unpublishing={unpublish.isPending}
          cancelling={cancel.isPending}
          onCheckIn={navigation.onCheckIn}
          onPublish={() => publish.mutate()}
          onUnpublish={() => unpublish.mutate()}
          onCancel={() => cancel.mutate()}
        />
      }
    />
  )
}

function EventWorkspace({
  event,
  navigation,
  taxRates,
  taxRatesState,
  mutationError,
  header,
}: {
  event: EventRecord
  navigation: EventDetailNavigation
  taxRates: TaxRateSummary[]
  taxRatesState: 'loading' | 'error' | 'ready'
  mutationError: Error | null
  header: ReactNode
}) {
  const viewOnly = useViewOnly()
  const owned = isOwnedEvent(event)
  const tabs: readonly EventTab[] = owned
    ? EVENT_TABS.filter((tab) => tab !== 'waitlist')
    : EVENT_TABS
  const [tab, setTab] = useEventTab(event.id, tabs)
  const canCreateOrders = !viewOnly && !owned && event.status !== 'cancelled'
  const reportActions = (
    <EventReportActions
      eventId={event.id}
      onReport={navigation.onRegistrationsReport}
      onSegment={navigation.onSegment}
    />
  )
  return (
    <DetailPage
      title={event.name}
      badge={<EventBadges event={event} />}
      subtitle={`${localStartLabel(event)} · ${ticketTypeCountLabel(event.ticket_types.length)}`}
      backHref="/events"
      backLabel="Events"
      onBack={navigation.onBack}
      readOnlyActions={reportActions}
      actions={
        <>
          {reportActions}
          {header}
        </>
      }
    >
      <div className="space-y-4">
        <OwnedBanner record={event} />
        {mutationError && <Alert>{eventApiMessage(mutationError)}</Alert>}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {tabs.map((value) => (
              <TabsTrigger key={value} value={value}>
                {TAB_LABELS[value]}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="orders">
            <EventOrders
              eventId={event.id}
              readOnly={owned}
              onNew={canCreateOrders ? navigation.onNewOrder : undefined}
            />
          </TabsContent>
          <TabsContent value="attendees" className="space-y-4">
            {event.status !== 'cancelled' && <EventTurnout event={event} />}
            <EventAttendees eventId={event.id} readOnly={owned} />
          </TabsContent>
          {!owned && (
            <TabsContent value="waitlist">
              <EventWaitlist eventId={event.id} />
            </TabsContent>
          )}
          <TabsContent value="settings">
            <EventSettings
              event={event}
              viewOnly={viewOnly}
              taxRates={taxRates}
              taxRatesState={taxRatesState}
              onDeleted={navigation.onDeleted}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DetailPage>
  )
}

const TAB_LABELS: Record<EventTab, string> = {
  orders: 'Orders',
  attendees: 'Attendees',
  waitlist: 'Waitlist',
  settings: 'Settings',
}

function EventBadges({ event }: { event: EventRecord }) {
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={event.status === 'published' ? 'success' : 'neutral'}>
        {titleize(event.status)}
      </Badge>
      {event.chapter && <Badge variant="neutral">{event.chapter.name}</Badge>}
    </div>
  )
}

function taxRateQueryState(isLoading: boolean, isError: boolean) {
  if (isLoading) return 'loading'
  if (isError) return 'error'
  return 'ready'
}

function ticketTypeCountLabel(count: number) {
  return `${count} ticket ${count === 1 ? 'type' : 'types'}`
}

function localStartLabel(event: EventRecord) {
  return event.time_zone_label ? `${event.schedule} ${event.time_zone_label}` : event.schedule
}

function titleize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
